/**
 * The wording of every SMS this system sends, editable without a deploy.
 *
 * ## Why this exists
 *
 * The sentences around a token were written in TypeScript, so changing "Key
 * the token into the meter as usual" to something a particular set of
 * landlords understands better meant a code change, a review and a deploy.
 * That is the wrong cost for a sentence, and the effect was that nobody
 * changed them — the wording stayed as first written, in a country where the
 * people receiving it read two languages and a good many read one better.
 *
 * ## What is deliberately *not* editable
 *
 * The facts. A template chooses the sentences; it cannot change a token, a
 * meter number or an amount, and it cannot leave them out by accident — see
 * `REQUIRED` below. A landlord who receives a beautifully worded message with
 * no token in it has received nothing, and that failure would be discovered
 * by a dark meter rather than by anything on this screen.
 *
 * ## Why placeholders are refused rather than ignored
 *
 * An unknown placeholder is a typo, and the two honest options are to refuse
 * it or to send `{tokn}` to a customer. Refusing it at the moment somebody
 * types it costs them a correction; ignoring it costs a message that reads as
 * broken software to the person who trusts you with their money.
 */
/** Every SMS this system sends, and who receives it. */
export const SMS_KINDS = [
    {
        id: 'power_token',
        label: 'Power token to the landlord',
        who: 'The landlord whose meter was paid',
        when: 'As soon as the token arrives from Kenya Power',
    },
    {
        id: 'pay_prompt',
        label: 'Payment details to the paying phone',
        who: 'Whoever is holding the handset that pays',
        when: 'When Pay is pressed',
    },
    {
        id: 'job_ack_reminder',
        label: 'Unacknowledged job reminder',
        who: 'The technician a job was dispatched to',
        when: 'When a dispatched job has not been acknowledged in time',
    },
];
/**
 * What a template may say, per kind.
 *
 * Named for what they mean rather than for the field they come from, because
 * the person editing these is reading them as English, not as a schema.
 */
export const SMS_PLACEHOLDERS = {
    power_token: [
        '{firstName}',
        '{landlordName}',
        '{company}',
        '{meterLabel}',
        '{meterNumber}',
        '{token}',
        '{units}',
        '{amount}',
    ],
    pay_prompt: ['{company}', '{paybill}', '{account}', '{amount}', '{meterLabel}', '{landlordName}'],
    job_ack_reminder: ['{company}', '{reference}', '{customerName}', '{link}'],
};
/**
 * The placeholders a template of each kind must contain.
 *
 * This is the guard that matters. Everything else about a template is taste;
 * a token message with no `{token}` in it is a message that cannot do its job,
 * and the only place to catch that is before it is saved.
 */
export const REQUIRED = {
    power_token: ['{token}', '{meterNumber}'],
    pay_prompt: ['{paybill}', '{account}', '{amount}'],
    job_ack_reminder: ['{reference}'],
};
/**
 * One segment is 160 characters of GSM-7, or 70 if anything falls outside it.
 *
 * Worth counting because a template is written once and sent thousands of
 * times: a single character tipping a message into two segments doubles the
 * bill quietly and for ever. A curly apostrophe does exactly that.
 */
const GSM = new Set("@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà\n\r");
/** Characters that cost two, in a GSM-7 message. */
const GSM_EXTENDED = new Set('^{}\\[~]|€');
export function segmentsFor(text) {
    const unicode = [...text].some((c) => !GSM.has(c) && !GSM_EXTENDED.has(c));
    if (unicode) {
        const units = [...text].length;
        return { unicode: true, units, segments: units <= 70 ? 1 : Math.ceil(units / 67) };
    }
    const units = [...text].reduce((sum, c) => sum + (GSM_EXTENDED.has(c) ? 2 : 1), 0);
    return { unicode: false, units, segments: units <= 160 ? 1 : Math.ceil(units / 153) };
}
/**
 * Why this template cannot be saved, or null.
 *
 * Returns one reason rather than a list, and the most serious one first. A
 * form that reports five problems at once gets read as noise; the person
 * fixes one and presses Save again, which is a conversation rather than an
 * interrogation.
 */
export function refuseSmsTemplate(kind, body) {
    const text = body.trim();
    if (text.length === 0)
        return null; /* Empty means "use the built-in wording". */
    const allowed = new Set(SMS_PLACEHOLDERS[kind]);
    for (const found of text.match(/\{[^{}]*\}/g) ?? []) {
        if (!allowed.has(found)) {
            return `${found} is not something this message knows about. You can use: ${SMS_PLACEHOLDERS[kind].join(', ')}.`;
        }
    }
    for (const needed of REQUIRED[kind]) {
        if (!text.includes(needed)) {
            return `Leave ${needed} in — without it the message cannot do its job.`;
        }
    }
    /*
     * A ceiling rather than a limit on segments. Four is already a long text to
     * receive; beyond that something has gone wrong with the writing rather
     * than with the intent, and it is worth stopping before it is sent a
     * thousand times.
     */
    const { segments } = segmentsFor(text);
    if (segments > 4) {
        return `That is ${segments} texts long. Shorten it — every recipient is charged for each one.`;
    }
    return null;
}
/**
 * Fill a template.
 *
 * A missing value removes the line it sits on rather than printing "null" or
 * an empty gap. Kenya Power states units *or* an amount depending on the
 * message, so a template naming both will regularly have only one — and
 * "Units: , KES ." is worse than saying neither.
 */
export function fillSmsTemplate(body, values) {
    return body
        .split('\n')
        .map((line) => {
        const used = line.match(/\{[^{}]*\}/g) ?? [];
        const missing = used.some((key) => {
            const value = values[key];
            return value === null || value === undefined || value === '';
        });
        /* A line that exists only to carry a value it has not got is dropped. */
        if (missing && used.length > 0)
            return null;
        return used.reduce((out, key) => out.split(key).join(values[key] ?? ''), line);
    })
        .filter((line) => line !== null)
        .join('\n')
        /* Collapse the hole a dropped line leaves behind. */
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
//# sourceMappingURL=sms-templates.js.map